import type { AssetNodeType } from '@bms2/shared';

/**
 * A node's draft, its blank and the body its save sends (piece 28), apart from the register's windows so that the
 * Add New window and a bill's asset window build the draft without loading the register's panels (T3600; decision 10641).
 */

export interface NodeDraft {
  name: string;
  nodeType: AssetNodeType;
  parentNodeId: string | null;
  parentLabel: string | null;
  /** The site stored on a node with no parent; a node with a parent stores none (decision 0780). */
  siteId: string | null;
  siteLabel: string | null;
  /** The site a node with a parent takes from the top of its tree, shown as a fact and never sent. */
  inheritedSiteLabel: string | null;
  serialNumber: string;
  assetTag: string;
  warrantyExpiresAt: string;
  acquiredOn: string;
  acquisitionCostCents: number | null;
  ownerCompanyId: string | null;
  ownerLabel: string | null;
  disposedOn: string;
  disposalProceedsCents: number | null;
  disposalReason: string;
}

/**
 * A blank node. 🔒 Beneath a parent it asks for no site and shows the one it
 * inherits; at the top it asks for one, prefilled with the site heading it was
 * pressed from (decision 0780).
 */
export function emptyNodeDraft(parent: { id: string; name: string; siteName?: string | null } | null, site: { id: string; label: string } | null = null): NodeDraft {
  return {
    name: '', nodeType: parent ? 'Component' : 'AssetGroup', parentNodeId: parent?.id ?? null, parentLabel: parent?.name ?? null,
    siteId: parent ? null : site?.id ?? null, siteLabel: parent ? null : site?.label ?? null, inheritedSiteLabel: parent ? parent.siteName ?? null : null,
    serialNumber: '', assetTag: '', warrantyExpiresAt: '', acquiredOn: '', acquisitionCostCents: null, ownerCompanyId: null, ownerLabel: null, disposedOn: '', disposalProceedsCents: null, disposalReason: '',
  };
}

/** The body a New or Edit save sends; 🔨 a site only for a node with no parent, because a child's is its root's (decision 0780). */
export function nodeBody(draft: NodeDraft) {
  return {
    name: draft.name.trim(), nodeType: draft.nodeType, ...(draft.parentNodeId ? {} : { siteId: draft.siteId }), serialNumber: draft.serialNumber || null, assetTag: draft.assetTag || null,
    warrantyExpiresAt: draft.warrantyExpiresAt || null, acquiredOn: draft.acquiredOn || null, acquisitionCostCents: draft.acquisitionCostCents,
  };
}
